import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ChevronRight, LogOut, Menu, Search, UserRound } from 'lucide-react';
import BrandLogo from '@/components/BrandLogo';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { useAuth } from '@/hooks/useAuth';
import { useCurrentPerson } from '@/hooks/identity/useCurrentPerson';
import { cn } from '@/lib/utils';
import {
  getActivePortalNavigationItem,
  getMobilePortalNavigationItems,
  getPortalPageTitle,
  isPortalNavigationItemActive,
  portalNavigationGroups,
  portalNavigationItems,
  type PortalNavigationItem,
} from './portalNavigation';

const PortalNavigationLink = ({
  item,
  pathname,
  compact = false,
  onNavigate,
}: {
  item: PortalNavigationItem;
  pathname: string;
  compact?: boolean;
  onNavigate?: () => void;
}) => {
  const Icon = item.icon;
  const isActive = isPortalNavigationItemActive(item, pathname);

  return (
    <NavLink
      to={item.path}
      onClick={onNavigate}
      aria-label={compact ? item.label : undefined}
      aria-current={isActive ? 'page' : undefined}
      title={compact ? item.label : undefined}
      className={cn(
        'flex min-h-11 items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        isActive
          ? 'bg-primary text-primary-foreground shadow-sm'
          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
        compact && 'mx-auto h-11 w-11 justify-center px-0',
      )}
    >
      <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
      {!compact && <span>{item.label}</span>}
    </NavLink>
  );
};

const PortalNavigationList = ({
  pathname,
  compact = false,
  onNavigate,
}: {
  pathname: string;
  compact?: boolean;
  onNavigate?: () => void;
}) => (
  <nav aria-label="Employee portal">
    {portalNavigationGroups.map(group => {
      const items = portalNavigationItems.filter(item => item.group === group.id);

      return (
        <div key={group.id} className="mb-5 last:mb-0">
          {!compact && (
            <p className="mb-2 px-3 text-xs font-semibold uppercase text-muted-foreground">
              {group.label}
            </p>
          )}
          <div className={cn('space-y-1', compact && 'space-y-2')}>
            {items.map(item => (
              <PortalNavigationLink
                key={item.id}
                item={item}
                pathname={pathname}
                compact={compact}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        </div>
      );
    })}
  </nav>
);

export const PortalShell = () => {
  const { user, profile, loading, signOut } = useAuth();
  const { person } = useCurrentPerson();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const pageTitle = getPortalPageTitle(location.pathname);
  const activeItem = getActivePortalNavigationItem(location.pathname);
  const mobileItems = getMobilePortalNavigationItems();

  const displayName = useMemo(() => {
    const personName = [person.firstName.value, person.lastName.value].filter(Boolean).join(' ');
    return personName || person.email.value.split('@')[0] || 'Portal user';
  }, [person.email.value, person.firstName.value, person.lastName.value]);

  const initials = displayName
    .split(/\s+/)
    .map(part => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  useEffect(() => {
    document.documentElement.dataset.appBottomNavigation = 'true';

    return () => {
      delete document.documentElement.dataset.appBottomNavigation;
    };
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background" role="status" aria-live="polite">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted border-b-primary" aria-hidden="true" />
        <span className="sr-only">Loading employee portal</span>
      </div>
    );
  }

  if (!user) {
    const returnPath = `${location.pathname}${location.search}`;
    return <Navigate to={`/auth?redirect=${encodeURIComponent(returnPath)}`} replace />;
  }

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth', { replace: true });
  };

  const openCommandPalette = () => {
    window.dispatchEvent(new Event('dialgia:open-command-palette'));
  };

  return (
    <div className="min-h-dvh bg-muted/20">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-20 border-r bg-background md:flex md:flex-col xl:w-64">
        <Link
          to="/portal"
          className="flex h-16 shrink-0 items-center justify-center border-b px-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring xl:justify-start xl:px-5"
          aria-label="Employee portal home"
        >
          <BrandLogo variant="icon" size={36} className="h-9 w-9 shrink-0 rounded-md" priority />
          <div className="ml-3 hidden min-w-0 xl:block">
            <p className="truncate text-sm font-semibold text-foreground">Dialgia</p>
            <p className="truncate text-xs text-muted-foreground">Employee portal</p>
          </div>
        </Link>

        <div className="min-h-0 flex-1 overflow-y-auto px-2 py-4 xl:px-3">
          <div className="xl:hidden">
            <PortalNavigationList pathname={location.pathname} compact />
          </div>
          <div className="hidden xl:block">
            <PortalNavigationList pathname={location.pathname} />
          </div>
        </div>

        <div className="border-t p-2 xl:p-3">
          <Link
            to="/profile"
            className="flex min-h-11 items-center justify-center rounded-md p-1.5 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring xl:justify-start xl:gap-3 xl:px-2"
            aria-label="Open profile"
          >
            <Avatar className="h-8 w-8 shrink-0">
              <AvatarImage src={profile?.avatar_url || undefined} alt="" />
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
            <div className="hidden min-w-0 xl:block">
              <p className="truncate text-sm font-medium">{displayName}</p>
              <p className="truncate text-xs text-muted-foreground">{person.email.value || user.email}</p>
            </div>
          </Link>
        </div>
      </aside>

      <div className="min-w-0 md:pl-20 xl:pl-64">
        <header className="sticky top-0 z-30 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/85">
          <div className="flex min-h-16 items-center gap-3 px-4 md:px-6">
            <Link
              to="/portal"
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:hidden"
              aria-label="Employee portal home"
            >
              <BrandLogo variant="icon" size={32} className="h-8 w-8 rounded-md" priority />
            </Link>

            <div className="min-w-0 flex-1">
              <div className="hidden items-center gap-1 text-xs text-muted-foreground sm:flex">
                <span>Employee portal</span>
                {activeItem?.path !== '/portal' && (
                  <>
                    <ChevronRight className="h-3 w-3" aria-hidden="true" />
                    <span className="truncate">{activeItem?.label ?? pageTitle}</span>
                  </>
                )}
              </div>
              <p className="truncate text-base font-semibold text-foreground sm:text-lg">{pageTitle}</p>
            </div>

            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-11 w-11 shrink-0"
              onClick={openCommandPalette}
              aria-label="Search pages and actions"
              title="Search"
            >
              <Search className="h-5 w-5" aria-hidden="true" />
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  className="h-11 max-w-44 gap-2 px-1.5 sm:px-2"
                  aria-label="Open account menu"
                >
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={profile?.avatar_url || undefined} alt="" />
                    <AvatarFallback>{initials}</AvatarFallback>
                  </Avatar>
                  <span className="hidden truncate text-sm font-medium lg:inline">{displayName}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel>
                  <span className="block truncate">{displayName}</span>
                  <span className="block truncate text-xs font-normal text-muted-foreground">{person.email.value || user.email}</span>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => navigate('/profile')} className="min-h-11">
                  <UserRound className="mr-2 h-4 w-4" aria-hidden="true" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={handleSignOut} className="min-h-11 text-destructive focus:text-destructive">
                  <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main id="main-content" className="min-w-0 overflow-x-clip pb-[calc(5rem+env(safe-area-inset-bottom,0px))] md:pb-0">
          <Outlet />
        </main>
      </div>

      <nav
        aria-label="Primary employee portal navigation"
        className="fixed inset-x-0 bottom-0 z-50 border-t bg-background/95 pb-[env(safe-area-inset-bottom,0px)] shadow-[0_-4px_16px_rgba(15,23,42,0.08)] backdrop-blur md:hidden"
      >
        <div className="grid h-16 grid-cols-5 px-1">
          {mobileItems.map(item => {
            const Icon = item.icon;
            const isActive = isPortalNavigationItemActive(item, location.pathname);

            return (
              <NavLink
                key={item.id}
                to={item.path}
                aria-current={isActive ? 'page' : undefined}
                className={cn(
                  'flex min-w-0 flex-col items-center justify-center gap-1 rounded-md px-1 text-[11px] font-medium',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                  isActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <Icon className="h-5 w-5" aria-hidden="true" />
                <span className="max-w-full truncate">{item.shortLabel}</span>
              </NavLink>
            );
          })}

          <Sheet open={isMoreOpen} onOpenChange={setIsMoreOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                className={cn(
                  'flex min-w-0 flex-col items-center justify-center gap-1 rounded-md px-1 text-[11px] font-medium',
                  'text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                  !mobileItems.some(item => isPortalNavigationItemActive(item, location.pathname)) && 'text-primary',
                )}
                aria-label="Open all portal sections"
              >
                <Menu className="h-5 w-5" aria-hidden="true" />
                <span>More</span>
              </button>
            </SheetTrigger>
            <SheetContent side="bottom" className="max-h-[85dvh] overflow-y-auto rounded-t-lg px-4 pb-[max(1rem,env(safe-area-inset-bottom,0px))]">
              <SheetHeader className="text-left">
                <SheetTitle>All portal sections</SheetTitle>
                <SheetDescription>Open work, documents, setup, and support.</SheetDescription>
              </SheetHeader>
              <div className="mt-5">
                <PortalNavigationList
                  pathname={location.pathname}
                  onNavigate={() => setIsMoreOpen(false)}
                />
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </div>
  );
};

export default PortalShell;
